import { z } from "zod";

import { socialPostScenePlanV1Schema } from "./social-post-scene.ts";
import { SOCIAL_POST_CAROUSEL_MAX_SLIDES } from "./social-post-carousel.ts";

export const DEFAULT_SOCIAL_POST_RENDER_TOKEN_TTL_MS = 5 * 60 * 1000;

export const socialPostRenderTokenPayloadSchema = z.object({
  v: z.literal(1),
  brandId: z.string().uuid(),
  scheduledPostId: z.string().uuid(),
  publicationId: z.string().uuid(),
  slideId: z.string().trim().min(1).max(80).nullable().optional(),
  slideIndex: z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES).nullable().optional(),
  nonce: z.string().min(1),
  exp: z.number().int().positive(),
});

export type SocialPostRenderTokenPayload = z.infer<typeof socialPostRenderTokenPayloadSchema>;

export const socialPostRenderLinkedAssetSchema = z.object({
  generatedAssetId: z.string().uuid(),
  blockId: z.string().min(1),
  role: z.string().min(1),
  position: z.number().int().nonnegative(),
  objectKey: z.string().min(1),
  mimeType: z.string().min(1),
  width: z.number().int().positive().nullable(),
  height: z.number().int().positive().nullable(),
  signedUrl: z.string().url(),
  prompt: z.string().nullable(),
  createdAt: z.string().datetime({ offset: true }),
});

export const socialPostRenderBrandkitSchema = z.object({
  logo: z
    .object({
      objectKey: z.string().min(1),
      signedUrl: z.string().url(),
    })
    .nullable(),
  fonts: z.array(
    z.object({
      family: z.string().min(1),
      source: z.enum(["brand_confirmed", "google_font"]),
      selectedVariant: z.string().min(1),
      loadUrl: z.string().url(),
      assetObjectKey: z.string().min(1).nullable(),
    }),
  ),
});

export const socialPostRenderPayloadSchema = z.object({
  brandId: z.string().uuid(),
  scheduledPostId: z.string().uuid(),
  publicationId: z.string().uuid(),
  slideId: z.string().trim().min(1).max(80).nullable().default(null),
  slideIndex: z.number().int().min(1).max(SOCIAL_POST_CAROUSEL_MAX_SLIDES).nullable().default(null),
  previewSource: z.enum(["published_draft", "generation_task"]),
  generationTaskId: z.string().uuid().nullable(),
  plan: socialPostScenePlanV1Schema,
  linkedAssets: z.array(socialPostRenderLinkedAssetSchema),
  brandkit: socialPostRenderBrandkitSchema.nullable(),
});

export type SocialPostRenderLinkedAsset = z.infer<typeof socialPostRenderLinkedAssetSchema>;
export type SocialPostRenderBrandkit = z.infer<typeof socialPostRenderBrandkitSchema>;
export type SocialPostRenderPayload = z.infer<typeof socialPostRenderPayloadSchema>;

export interface SocialPostRenderTokenOptions {
  secret: string;
  now?: Date;
  ttlMs?: number;
}

const encoder = new TextEncoder();

const toBase64Url = (value: string | ArrayBuffer): string => {
  const bytes = typeof value === "string" ? encoder.encode(value) : new Uint8Array(value);
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }

  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replaceAll("=", "");
};

const fromBase64Url = (value: string): string => {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), "=");
  const binary = atob(padded.replaceAll("-", "+").replaceAll("_", "/"));
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return new TextDecoder().decode(bytes);
};

const sign = async (payload: string, secret: string): Promise<string> => {
  const key = await crypto.subtle.importKey(
    "raw",
    encoder.encode(secret),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  return toBase64Url(await crypto.subtle.sign("HMAC", key, encoder.encode(payload)));
};

export const createSocialPostRenderToken = async (
  input: Omit<SocialPostRenderTokenPayload, "v" | "nonce" | "exp">,
  options: SocialPostRenderTokenOptions,
): Promise<string> => {
  const now = options.now ?? new Date();
  const payload: SocialPostRenderTokenPayload = {
    v: 1,
    brandId: input.brandId,
    scheduledPostId: input.scheduledPostId,
    publicationId: input.publicationId,
    ...(input.slideId ? { slideId: input.slideId } : {}),
    ...(input.slideIndex ? { slideIndex: input.slideIndex } : {}),
    nonce: crypto.randomUUID(),
    exp: now.getTime() + (options.ttlMs ?? DEFAULT_SOCIAL_POST_RENDER_TOKEN_TTL_MS),
  };
  const encodedPayload = toBase64Url(JSON.stringify(payload));
  return `${encodedPayload}.${await sign(encodedPayload, options.secret)}`;
};

export const verifySocialPostRenderToken = async (
  value: string | null,
  options: Pick<SocialPostRenderTokenOptions, "secret" | "now">,
): Promise<SocialPostRenderTokenPayload | null> => {
  if (!value) {
    return null;
  }

  const [encodedPayload, signature, ...rest] = value.split(".");
  if (!encodedPayload || !signature || rest.length > 0) {
    return null;
  }

  const expectedSignature = await sign(encodedPayload, options.secret);
  if (signature !== expectedSignature) {
    return null;
  }

  try {
    const parsed = socialPostRenderTokenPayloadSchema.safeParse(
      JSON.parse(fromBase64Url(encodedPayload)),
    );
    if (!parsed.success) {
      return null;
    }

    if (parsed.data.exp < (options.now ?? new Date()).getTime()) {
      return null;
    }

    return parsed.data;
  } catch {
    return null;
  }
};
